import React from 'react';
import { BarChart3, Users, Globe, MessageCircle, TrendingUp, Activity } from 'lucide-react';

interface AnalyticsLoadingCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
  type?: 'metrics' | 'audience' | 'comments' | 'platform';
  className?: string;
}

const AnalyticsLoadingCard: React.FC<AnalyticsLoadingCardProps> = ({ 
  title, 
  subtitle = "Fetching your social media data...", 
  icon: Icon = BarChart3,
  type = 'metrics',
  className = ''
}) => {
  const getLoadingElements = () => {
    switch (type) {
      case 'metrics':
        return (
          <div className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-20 mb-2"></div>
                      <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Chart Placeholder */}
            <div className="h-64 bg-gray-100 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
          </div>
        );
      
      case 'audience':
        return (
          <div className="space-y-6">
            {/* Demographics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                      <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64 bg-gray-100 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
              <div className="h-64 bg-gray-100 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
            </div>
          </div>
        );
      
      case 'comments':
        return (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start space-x-4 p-3 rounded-xl animate-pulse">
                <div className="w-6 h-6 bg-gray-200 dark:bg-slate-700 rounded-full flex-shrink-0"></div>
                <div className="flex-grow">
                  <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full mb-2"></div>
                  <div className="flex justify-between items-center">
                    <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'platform':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-100 dark:bg-slate-800/50 rounded-xl p-4 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-20 mb-1"></div>
                    <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-center py-2">
                  <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-16 mx-auto mb-1"></div>
                  <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-12 mx-auto"></div>
                </div>
                <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded mt-4"></div>
              </div>
            ))}
          </div>
        );
      
      default:
        return <div className="h-32 bg-gray-100 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>;
    }
  };

  return (
    <div className={`bg-white dark:bg-slate-900/70 rounded-2xl p-6 border border-gray-200/80 dark:border-slate-800 shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-center mb-8">
        <div className="text-center">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ 
              background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)',
              boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
            }}
          >
            <Icon className="w-6 h-6 text-white animate-pulse" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
          
          {/* Loading Dots */}
          <div className="flex justify-center space-x-1 mt-4">
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#30b3dd' }}></div>
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#30b3dd', animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#30b3dd', animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>

      {/* Loading Content */}
      {getLoadingElements()}
    </div>
  );
};

export default AnalyticsLoadingCard;